import { Message, MessageSender, MessageAttachment, MessageReactions, ReadReceipt } from '../types';
import { getUserIdMapping } from '@/core/auth/services/userIdentity';

export interface ApiMessageLike {
  id?: string;
  conversationId?: string;
  sender?: MessageSender | Record<string, unknown> | string;
  senderId?: string | null;
  userId?: string;
  content?: string | null;
  text?: string;
  message?: string;
  sentAt?: string | null;
  timestamp?: number;
  attachments?: MessageAttachment[] | unknown[];
  mediaUrls?: string[];
  replyTo?: string | null;
  parentMessageId?: string | null;
  reactions?: MessageReactions;
  messageType?: string;
  status?: string;
  metadata?: Record<string, unknown> | null;
  readBy?: ReadReceipt[];
  tempId?: string;
  [k: string]: unknown;
}

function getSenderObj(sender: MessageSender | Record<string, unknown> | string | undefined): MessageSender | undefined {
  if (!sender) return undefined;
  if (typeof sender === 'string') return undefined;
  // Narrow Record<string, unknown> to MessageSender by checking for required 'id' field
  if ('id' in sender && typeof sender.id === 'string') {
    return sender as MessageSender;
  }
  return undefined;
}

export function transformTeamApiMessage(raw: ApiMessageLike): Message {
  const senderObj = getSenderObj(raw.sender);

  let senderId =
    senderObj?.id ||
    raw.senderId ||
    raw.userId ||
    '';

  // If the backend sent the auth ID for the current user, translate to API ID for consistency
  try {
    const mapping = getUserIdMapping();
    if (mapping && senderId === mapping.authId) {
      senderId = mapping.apiId;
    }
  } catch {}

  // Normalize to string to ensure consistent comparisons in UI
  if (senderId != null) {
    senderId = String(senderId).trim();
  } else {
    senderId = '';
  }

  const content = raw.content || raw.text || raw.message || '';

  const timestamp = typeof raw.timestamp === 'number'
    ? raw.timestamp
    : raw.sentAt
      ? new Date(raw.sentAt).getTime()
      : (typeof window !== 'undefined' ? Date.now() : 0);

  const normalized: Message = {
    id: raw.id || '',
    conversationId: raw.conversationId || '',
    senderId,
    content,
    messageType: raw.messageType || 'text',
    status: raw.status || 'sent',
    parentMessageId: raw.parentMessageId || null,
    metadata: raw.metadata || null,
    timestamp,
    attachments: Array.isArray(raw.attachments)
      ? raw.attachments as MessageAttachment[]
      : Array.isArray(raw.mediaUrls)
        ? raw.mediaUrls.map((url: string, idx: number) => ({
            id: `media_${raw.id}_${idx}`,
            type: 'image' as const,
            url,
            name: url.split('/').pop() || `media_${idx}`
          }))
        : [],
    replyTo: raw.replyTo || raw.parentMessageId || undefined,
    sender: senderObj,
    reactions: raw.reactions || {},
    readBy: raw.readBy || [],
    tempId: raw.tempId
  };

  return normalized;
}
